import { useEffect } from 'react';
import { Alert, Form, Modal, Select, Skeleton, Switch, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getTeamSettings, updateTeamSettings } from '@/services/teamAssignments.service';
import EinvoiceReminderSettingsSection from '@/components/team-assignments/EinvoiceReminderSettingsSection';
import { isValidEmail } from '@/components/terminate-action-modal/terminationShared';

/**
 * Who manages the assignments (besides superAdmin/techAdmin), who is copied
 * on every client mailing built on these contacts, who is told of team changes
 * to make them in AB Soft and who gets an overview of every change by mail.
 */
export default function SettingsModal({ open, onClose }) {
  const { t } = useTranslation(['teamAssignments', 'teamContacts', 'teamBilling']);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['team-settings'], queryFn: getTeamSettings, enabled: open });

  useEffect(() => {
    if (!data) return;
    form.setFieldsValue({
      managers: data.managers.map((u) => u._id),
      alwaysCc: data.alwaysCc.map((u) => u._id),
      abNotify: (data.abNotify || []).map((p) => p._id),
      changeNotify: (data.changeNotify || []).map((u) => u._id),
      changeNotifyEmails: data.changeNotifyEmails || [],
      contactsMailingEnabled: !!data.contactsMailingEnabled,
      billingRecipients: (data.billingRecipients || []).map((u) => u._id),
      billingRemindersEnabled: !!data.billingRemindersEnabled,
    });
  }, [data, form]);

  const save = useMutation({
    mutationFn: (v) => updateTeamSettings(data.canEditManagers ? v : { alwaysCc: v.alwaysCc, abNotify: v.abNotify, changeNotify: v.changeNotify, changeNotifyEmails: v.changeNotifyEmails, billingRecipients: v.billingRecipients, billingRemindersEnabled: v.billingRemindersEnabled }),
    onSuccess: () => {
      message.success(t('messages.saved'));
      queryClient.invalidateQueries({ queryKey: ['team-settings'] });
      queryClient.invalidateQueries({ queryKey: ['team-board'] });
      queryClient.invalidateQueries({ queryKey: ['team-billing-options'] });
      queryClient.invalidateQueries({ queryKey: ['team-billing'] });
      onClose();
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const options = (data?.candidates || []).map((u) => ({ value: u._id, label: `${u.name} (${u.email})` }));
  const billingOptions = (data?.billingCandidates || []).map((u) => ({ value: u._id, label: `${u.name} (${u.email})` }));
  const teamOptions = (data?.teamCandidates || []).map((p) => ({
    value: p._id,
    label: p.email ? `${p.name} (${p.email})` : p.name,
  }));

  return (
    <Modal
      open={open}
      title={t('settings.title')}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t('actions.save')}
      cancelText={t('actions.cancel')}
      confirmLoading={save.isPending}
      okButtonProps={{ shape: 'round' }}
      cancelButtonProps={{ shape: 'round' }}
    >
      {isLoading || !data ? (
        <Skeleton active />
      ) : (
        <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
          <p style={{ opacity: 0.75, marginTop: 0 }}>{t('settings.intro')}</p>
          <Form.Item name="managers" label={t('settings.managers')} extra={t('settings.managersHint')}>
            <Select mode="multiple" showSearch optionFilterProp="label" options={options} disabled={!data.canEditManagers} />
          </Form.Item>
          <Form.Item name="alwaysCc" label={t('settings.alwaysCc')} extra={t('settings.alwaysCcHint')}>
            <Select mode="multiple" showSearch optionFilterProp="label" options={options} />
          </Form.Item>
          <Form.Item name="abNotify" label={t('settings.abNotify')} extra={t('settings.abNotifyHint')}>
            <Select mode="multiple" showSearch optionFilterProp="label" options={teamOptions} />
          </Form.Item>
          <Form.Item name="changeNotify" label={t('settings.changeNotify')} extra={t('settings.changeNotifyHint')}>
            <Select mode="multiple" showSearch optionFilterProp="label" options={options} />
          </Form.Item>
          <Form.Item
            name="changeNotifyEmails"
            label={t('settings.changeNotifyEmails')}
            extra={t('settings.changeNotifyEmailsHint')}
            rules={[
              {
                validator: (_, value) =>
                  (value || []).every((address) => isValidEmail(address))
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('settings.changeNotifyEmailsInvalid'))),
              },
            ]}
          >
            <Select mode="tags" tokenSeparators={[',', ';', ' ']} open={false} suffixIcon={null} />
          </Form.Item>
          <Form.Item name="billingRecipients" label={t('settings.recipients', { ns: 'teamBilling' })} extra={t('settings.recipientsHint', { ns: 'teamBilling' })}>
            <Select mode="multiple" showSearch optionFilterProp="label" options={billingOptions} />
          </Form.Item>
          <Form.Item
            name="billingRemindersEnabled"
            valuePropName="checked"
            label={t('settings.switch', { ns: 'teamBilling' })}
            extra={t('settings.switchHint', { ns: 'teamBilling' })}
          >
            <Switch />
          </Form.Item>
          {data.canEditManagers && (
            <Form.Item
              name="contactsMailingEnabled"
              valuePropName="checked"
              label={t('source.globalSwitch', { ns: 'teamContacts' })}
              extra={t('source.globalSwitchHint', { ns: 'teamContacts' })}
            >
              <Switch />
            </Form.Item>
          )}
          {!data.canEditManagers && data.contactsMailingEnabled && (
            <Alert className="mb-3" type="info" showIcon message={t('source.globalOn', { ns: 'teamContacts' })} />
          )}
          <p style={{ margin: 0 }}>
            {t('settings.ownCompany')}: <b>{data.ownCompany?.name || t('settings.ownCompanyAuto')}</b>
          </p>
          <p style={{ opacity: 0.6, margin: 0, fontSize: 12 }}>{t('settings.ownCompanyHint')}</p>
          <div style={{ marginTop: 16 }}>
            <EinvoiceReminderSettingsSection />
          </div>
        </Form>
      )}
    </Modal>
  );
}
